// La borne des 6 mois.
//
// Décision de Julien le 2026-08-28 : une session mobile ne meurt plus à la fermeture
// de l'app, mais elle ne doit pas non plus vivre éternellement sur un appareil oublié.
// Mesuré en prod le même jour : sur 6300 sessions, AUCUNE n'a dépassé 6 mois
// d'inactivité (maximum constaté 177 jours), alors qu'un seuil à 30 jours en aurait
// coupé 2169. Le seuil est donc invisible en pratique tout en posant une limite.
//
// ⚠️ Ce n'est PAS un contrôle de sécurité : l'horloge de l'appareil est manipulable.
// C'est une borne de confort. Ne jamais s'y fier comme à un verrou.
//
// Toutes les fonctions ci-dessous préfèrent l'inaction au faux positif : en cas de
// doute (horodatage illisible, coffre muet, horloge qui recule), on NE déconnecte pas.
// Déconnecter à tort est exactement le problème que ce chantier corrige.

import { isNative } from '@/lib/isNativePlatform';
import { readNativeRaw, writeNativeRaw, LAST_USED_KEY } from '@/integrations/supabase/nativeAuthStore';

/** 182 jours, soit six mois. */
export const INACTIVITY_WINDOW_MS = 182 * 24 * 60 * 60 * 1000;

/** Note que l'app vient d'être utilisée. Ne jette jamais. */
export async function touchNativeLastUsed(now: number = Date.now()): Promise<void> {
  if (!isNative()) return;
  try {
    await writeNativeRaw(LAST_USED_KEY, String(now));
  } catch { /* une note manquée ne doit jamais casser le démarrage */ }
}

/**
 * La session dort-elle depuis plus de six mois ? Ne jette jamais.
 *
 * Pose ELLE-MÊME le poinçon `flowcut-last-used-at` juste après avoir calculé son
 * verdict, avant de le rendre. C'est délibéré et les deux ne doivent JAMAIS se
 * reséparer : `AuthProvider` monte deux effets, l'un appelle cette fonction (qui
 * LIT le poinçon), l'autre démarre `startNativeAutoRefresh` (qui autrefois
 * l'ÉCRIVAIT, au montage). Les deux couraient l'un contre l'autre, départagés
 * uniquement par l'ordre de déclaration des `useEffect`. Si l'écriture avait gagné,
 * la garde aurait toujours lu un horodatage tout frais et rendu `false` : la borne
 * des 6 mois ne se serait plus jamais déclenchée, en silence, sans qu'aucun test ne
 * rougisse. En fusionnant lecture et écriture dans cette seule fonction, il n'y a
 * plus deux modules à faire courir l'un contre l'autre : l'ordre ne peut plus
 * s'inverser.
 */
export async function hasExceededInactivityWindow(now: number = Date.now()): Promise<boolean> {
  if (!isNative()) return false;

  // Pose le poinçon puis rend le verdict. Utilisée par CHAQUE sortie de fonction
  // sous ce point (premier lancement, horodatage illisible, horloge qui recule, et
  // le verdict normal) : `AuthProvider` n'appelle cette fonction qu'au montage, donc
  // chaque appel représente « l'app vient de s'ouvrir », que le verdict soit
  // d'ouvrir la session ou de la clore. Dans le cas « expirée », le prochain appel
  // de `clearNativeAuthStore()` efface de toute façon cet horodatage aussitôt.
  const rendre = async (verdict: boolean): Promise<boolean> => {
    await touchNativeLastUsed(now);
    return verdict;
  };

  let brut: string | null = null;
  try {
    brut = await readNativeRaw(LAST_USED_KEY);
  } catch {
    return rendre(false);
  }
  if (brut === null) return rendre(false);

  const dernier = Number(brut);
  // `Number('')` rend 0, pas NaN : sans la borne basse, un horodatage vide ou
  // corrompu se lirait comme l'époque Unix et déclencherait une déconnexion.
  // Un horodatage réel est toujours strictement positif.
  if (!Number.isFinite(dernier) || dernier <= 0) return rendre(false);

  const ecart = now - dernier;
  // Un écart négatif veut dire que l'horloge a reculé : on ne déconnecte pas.
  if (ecart <= 0) return rendre(false);

  return rendre(ecart > INACTIVITY_WINDOW_MS);
}
